import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useContext, useMemo, useState} from 'react';
import {Redirect} from 'react-router';

import {envIsPublicCloud} from '../../../config';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {useViewer} from '../../../state/viewer/hooks';
import {
  useRampFlagRegistrySearch,
  useRampFlagStarredRegistries,
} from '../../../util/rampFeatureFlags';
import * as urls from '../../../util/urls';
import {AccountSelectorContext} from '../../Search/SearchNav/AccountSelectorContextProvider';
import {InstrumentedCenteredWaveLoader as Loader} from '../../utility/InstrumentedLoader';
import {useIsOrgReadOnly, useRegistryProject} from '../common/hooks';
import {SingleRegistrySearchContextProvider} from '../common/RegistriesSearchContextProvider';
import {StarredRegistriesContext} from '../common/StarredRegistriesContextProvider';
import {FindMigratedRegistryBanner} from '../ModelRegistryMigration/MigratedRegistryBanners/FindMigratedRegistryBanner';
import {RegistryPage} from '../Registry/RegistryPage';
import {RegistrySettingsPage} from '../RegistrySettings/RegistrySettingsPage';
import {CreateRegistryDrawer} from '../workflows/CreateRegistryDrawer/CreateRegistryDrawer';
import {useSetupDefaultRegistries} from '../workflows/CreateRegistryDrawer/hooks';
import {AllRegistries} from './AllRegistries';
import {useRegistriesWithMembership} from './hooks';
import {InitiateRegstriesSetup} from './InitiateRegstriesSetup';
import {RegistryCollections} from './RegistriesCollections';
import {RegistriesHomePageHeader} from './RegistriesHomePageHeader';
import {RegistriesInfoBanner} from './RegistriesInfoBanner';
import RegistriesSearchBar from './RegistriesSearchBar';
import StarredRegistriesSection from './StarredRegistriesSection';

type RegistriesHomepageProps = {
  registryName?: string;
  loadSettingsPage: boolean;
};

export const RegistriesHomepage = ({
  registryName,
  loadSettingsPage,
}: RegistriesHomepageProps) => {
  const viewer = useViewer();
  const userId = viewer?.id;
  const userName = viewer?.username;
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;

  const {data: isRegistrySearchEnabled} = useRampFlagRegistrySearch(orgName);

  // Temporary
  // The CreateRegistryDrawer checks for existing registry names, so we populate them here.
  // But this is limited by pagination, since we're not fetching all registries at once.
  const [existingRegistryNames, setExistingRegistryNames] = useState<
    Set<string>
  >(new Set());
  const [isCreateRegistryDrawerOpen, setIsCreateRegistryDrawerOpen] =
    useState(false);

  const {
    defaultRegistries,
    loading: isSetupRegistriesLoading,
    orgId,
    entityName,
  } = useSetupDefaultRegistries(orgName);
  const {
    isOrganizationReadOnly: readOnly,
    loading: isOrganizationReadOnlyLoading,
  } = useIsOrgReadOnly();

  const showStarredRegistriesSection = useRampFlagStarredRegistries(orgId);

  const {data: registryProjectData, loading: isRegistryProjectLoading} =
    useRegistryProject(orgName, registryName);

  const {
    starredRegistries: starredRegistriesMap,
    isLoading: isStarredRegistriesLoading,
  } = useContext(StarredRegistriesContext);

  const starredRegistries = useMemo(
    () => Object.values(starredRegistriesMap),
    [starredRegistriesMap]
  );

  const {registriesWithMembership: starredRegistriesWithMembership} =
    useRegistriesWithMembership({
      orgName,
      registries: starredRegistries,
      viewerId: viewer?.id,
    });

  if (
    isOrganizationReadOnlyLoading ||
    isRegistryProjectLoading ||
    isSetupRegistriesLoading
  ) {
    return <Loader name="global-registry-landing-page" size="huge" />;
  }

  if (registryName != null) {
    const registry = defaultRegistries.has(registryName)
      ? defaultRegistries.get(registryName)
      : registryProjectData;
    if (registry == null) {
      return <Redirect to={urls.globalRegistry(orgName)} />;
    }
    if (loadSettingsPage) {
      return <RegistrySettingsPage registry={registry} />;
    }
    return (
      <SingleRegistrySearchContextProvider>
        <RegistryPage registry={registry} />
      </SingleRegistrySearchContextProvider>
    );
  }

  if (readOnly && defaultRegistries.size === 0) {
    return (
      <Tailwind style={{height: '100%', width: '100%'}}>
        <RegistriesHomePageHeader
          showCreateRegistryButton={!readOnly && isRegistrySearchEnabled}
          onCreateRegistry={() => {}}
        />

        <RegistriesInfoBanner
          orgName={orgName}
          orgID={orgId ?? ''}
          userName={userName ?? ''}
          userID={userId ?? ''}
        />
        <InitiateRegstriesSetup />
      </Tailwind>
    );
  }

  return (
    <Tailwind style={{height: '100%', width: '100%'}}>
      <div className="flex h-full w-full flex-col bg-moon-50">
        <RegistriesHomePageHeader
          showCreateRegistryButton={!readOnly && isRegistrySearchEnabled}
          onCreateRegistry={() => setIsCreateRegistryDrawerOpen(true)}
        />

        <RegistriesInfoBanner
          orgName={orgName}
          orgID={orgId ?? ''}
          userName={userName ?? ''}
          userID={userId ?? ''}
        />

        <FindMigratedRegistryBanner
          orgID={orgId ?? ''}
          orgName={orgName ?? ''}
          userID={userId ?? ''}
        />

        {isRegistrySearchEnabled && <RegistriesSearchBar />}

        {showStarredRegistriesSection && (
          <StarredRegistriesSection
            loading={isStarredRegistriesLoading}
            starredRegistries={starredRegistriesWithMembership}
            readOnly={readOnly}
          />
        )}

        <AllRegistries
          orgName={orgName}
          readOnly={readOnly}
          setExistingRegistryNames={setExistingRegistryNames}
        />
        {isRegistrySearchEnabled && <RegistryCollections orgName={orgName} />}
      </div>
      <CreateRegistryDrawer
        open={isCreateRegistryDrawerOpen}
        closeDrawer={() => setIsCreateRegistryDrawerOpen(false)}
        entityName={entityName}
        existingRegistryNames={existingRegistryNames}
        organizationName={orgName}
        organizationId={orgId}
      />
    </Tailwind>
  );
};
